/**
 * CharacterArt — theme character art with two presentation variants.
 *
 *   • `'mascot'` (default) — fills the sidebar's bottom slot. The image
 *     uses `object-fit: contain` to stay fully visible inside its
 *     parent. Used by `ThreadSidebar`.
 *   • `'watermark'` — pinned to the bottom-right of the chat surface
 *     with low opacity and `pointer-events: none`. Used on mobile so
 *     the active theme's character is still visible when the sidebar
 *     drawer is closed.
 *
 * PNG sources go through `cleanCharacterImage` to strip stock-photo
 * backgrounds (white margins, baked-in checkerboard, gradients) before
 * display.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { motion } from 'framer-motion';

import { useThemeStore, useThreadsStore } from '../../state';

import { cleanCharacterImage } from './cleanCharacterImage';
import styles from './CharacterArt.module.css';

const FALLBACK_IDLE_MS = 1500;

export interface CharacterArtProps {
  /** Presentation variant. Defaults to `'mascot'`. */
  variant?: 'mascot' | 'watermark';
}

export function CharacterArt({
  variant = 'mascot',
}: CharacterArtProps): ReactElement | null {
  const theme = useThemeStore((s) => s.current);
  const messages = useThreadsStore((s) => s.activeThread?.messages ?? []);

  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const [reacting, setReacting] = useState<boolean>(false);
  const lastMessageCount = useRef<number>(messages.length);

  // Load character art for the active theme.
  useEffect(() => {
    let cancelled = false;
    void theme.assets.characterArt().then((mod) => {
      if (!cancelled) setImgUrl(mod.default);
    });
    return () => {
      cancelled = true;
      setProcessedUrl(null);
    };
  }, [theme.id, theme.assets]);

  // Run the background remover for PNG sources. SVG passes through.
  useEffect(() => {
    if (imgUrl === null) return;
    const isPng = imgUrl.toLowerCase().includes('.png');
    if (!isPng) {
      setProcessedUrl(imgUrl);
      return;
    }
    let cancelled = false;
    void cleanCharacterImage(imgUrl).then((url) => {
      if (!cancelled) setProcessedUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [imgUrl]);

  // Reaction animation when the user posts a message.
  useEffect(() => {
    const prev = lastMessageCount.current;
    lastMessageCount.current = messages.length;
    if (messages.length <= prev) return;
    const last = messages[messages.length - 1];
    if (last === undefined || last.role !== 'user') return;
    if (theme.animation.characterIdle.enabled === false) return;

    const duration =
      theme.animation.characterIdle.duration > 0
        ? theme.animation.characterIdle.duration
        : FALLBACK_IDLE_MS;
    setReacting(true);
    const t = window.setTimeout(() => setReacting(false), duration);
    return () => window.clearTimeout(t);
  }, [messages.length, theme.animation.characterIdle]);

  const displayUrl = processedUrl ?? imgUrl;
  if (displayUrl === null) return null;

  const className =
    variant === 'watermark' ? styles.watermark : styles.character;

  const idleEnabled = theme.animation.characterIdle.enabled !== false;
  const idleSec =
    (theme.animation.characterIdle.duration > 0
      ? theme.animation.characterIdle.duration
      : FALLBACK_IDLE_MS) / 1000;

  if (!idleEnabled) {
    return (
      <img
        src={displayUrl}
        alt=""
        className={className}
        aria-hidden="true"
        data-testid="character-art"
        data-variant={variant}
      />
    );
  }

  return (
    <motion.img
      src={displayUrl}
      alt=""
      className={className}
      aria-hidden="true"
      data-testid="character-art"
      data-variant={variant}
      data-reacting={reacting ? 'true' : 'false'}
      animate={
        reacting
          ? { y: [0, -10, 0], rotate: [0, 3, -3, 0], scale: [1, 1.04, 1] }
          : { y: [0, -4, 0] }
      }
      transition={{
        duration: idleSec,
        repeat: reacting ? 0 : Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}
